import groq from "groq";

/**
 * GROQ projection that builds a full URL path by walking up the parent chain.
 * Handles both site-root nodes (returns "/") and nested nodes.
 *
 * @todo:
 *  - This projection is duplicated in build-path-projection.ts. We should
 *    consolidate them into a single projection.
 *  - build-path-projection.ts is dynamically generated based on the configured
 *    max depth. This should match.
 */
const fullPathProjection = `
  "fullPath": select(
    nodeRole == "site-root" => "/",
    true => "/" + array::join(
      array::compact([
        parent->parent->parent->parent->parent->parent->parent->slug.current,
        parent->parent->parent->parent->parent->parent->slug.current,
        parent->parent->parent->parent->parent->slug.current,
        parent->parent->parent->parent->slug.current,
        parent->parent->parent->slug.current,
        parent->parent->slug.current,
        parent->slug.current,
        slug.current
      ]),
      "/"
    ) + "/"
  )
`;

/**
 * Parent ref chain for site-membership filtering. Used by both allRoutes and
 * branchParents so only routes under the current site root are included.
 * @todo: should be dynamic based on the max depth
 */
const parentChainProjection = `
  "parent1": parent._ref,
  "parent2": parent->parent._ref,
  "parent3": parent->parent->parent._ref,
  "parent4": parent->parent->parent->parent._ref,
  "parent5": parent->parent->parent->parent->parent._ref,
  "parent6": parent->parent->parent->parent->parent->parent._ref,
  "parent7": parent->parent->parent->parent->parent->parent->parent._ref
`;

/**
 * Predicate for filtering route nodes to the current site (^ = result of
 * previous pass; must have site.rootId). Use in array filter: arr[${siteFilter}]
 */
const siteFilter = `
  _id == ^.site.rootId ||
  parent1 == ^.site.rootId ||
  parent2 == ^.site.rootId ||
  parent3 == ^.site.rootId ||
  parent4 == ^.site.rootId ||
  parent5 == ^.site.rootId ||
  parent6 == ^.site.rootId ||
  parent7 == ^.site.rootId
`;

/**
 * GROQ projection for branch route node fields shared by both branch roots
 * and their children in the sitemap query. Computes `lastModified` as the most
 * recent of the route node and its linked page — consistent with how
 * hierarchical routes derive their `lastModified`.
 */
const branchNodeProjection = `
  "slug": slug.current,
  "lastModified": select(
    defined(page->_updatedAt) && page->_updatedAt >= _updatedAt => page->_updatedAt,
    true => _updatedAt
  ),
  hideFromSitemap,
  isEnabled,
  "hasPage": defined(page)
`;

/**
 * Nested route children for branch sitemap expansion. Unrolled to match
 * {@link DEFAULT_ROUTE_CHILD_TREE_DEPTH} because GROQ cannot recurse and
 * Sanity TypeGen cannot evaluate dynamic projection builders.
 */
const sitemapBranchChildTreeProjection = `
  "children": *[_type == "routeNode" && parent._ref == ^._id] {
    ${branchNodeProjection},
    "children": *[_type == "routeNode" && parent._ref == ^._id] {
      ${branchNodeProjection},
      "children": *[_type == "routeNode" && parent._ref == ^._id] {
        ${branchNodeProjection},
        "children": *[_type == "routeNode" && parent._ref == ^._id] {
          ${branchNodeProjection},
          "children": []
        }
      }
    }
  }
`;

/**
 * GROQ query to fetch all enabled route nodes with their timestamps, parent refs,
 * and full path. Also fetches branch roots and their children, plus parents that
 * link to them.
 *
 * @note SitemapQueryResult type is auto-generated in sanity.types.ts via typegen
 */
const SitemapQuery = groq`{
  // Get the site's root node
  "site": *[_type == "siteNode" && siteId == $siteId][0] {
    siteId,
    "rootId": root._ref,
    "rootTitle": root->title
  },
  
  // First pass: Get all enabled routeNodes with their timestamps
  "allRoutes": *[_type == "routeNode" && isEnabled == true && defined(page._ref)] {
    _id,
    title,
    "slug": slug.current,
    isEnabled,
    nodeRole,
    "hasPage": defined(page._ref),

    // Keep source timestamps so we can choose the most recent
    "routeUpdatedAt": _updatedAt,
    "pageUpdatedAt": page->_updatedAt,
    // if we care about other types of context, we will need to revise this part
    "officeContentUpdatedAt": context[_type == "officeContentRef"]-> | order(_updatedAt desc)[0]._updatedAt,
    // Capture parent refs for site membership check
    ${parentChainProjection},
    ${fullPathProjection}
  },

  // Branch routes: fetch branch roots and their children, plus parents that link to them (flattened for site filtering in third pass)
  "branchRoots": *[_type == "routeNode" && nodeRole == "branch-root"] {
    _id,
    ${branchNodeProjection},
    ${sitemapBranchChildTreeProjection}
  },
  "branchParents": *[_type == "routeNode" && defined(linkedBranchRoots) && isEnabled == true] {
    "branchRootIds": linkedBranchRoots[]._ref,
    ${parentChainProjection},
    ${fullPathProjection}
  }
} {
  // Second pass: compute lastModifiedBy now that timestamps are available
  site,
  branchRoots,
  branchParents,
  "allRoutes": allRoutes[] {
    ...,
    "lastModifiedBy": select(
      defined(officeContentUpdatedAt) &&
        officeContentUpdatedAt >= coalesce(pageUpdatedAt, "1970-01-01T00:00:00Z") &&
        officeContentUpdatedAt >= coalesce(routeUpdatedAt, "1970-01-01T00:00:00Z") =>
        "officeContent",
      defined(pageUpdatedAt) &&
        pageUpdatedAt >= coalesce(routeUpdatedAt, "1970-01-01T00:00:00Z") =>
        "page",
      defined(routeUpdatedAt) => "routeNode",
      true => null
    )
  }
} {
  // Third pass: filter to site and compute lastModified
  "branchRoutes": {
    branchRoots,
    "parents": branchParents[${siteFilter}] {
      "branchRootIds": branchRootIds,
      fullPath
    }
  },
  "routes": allRoutes[${siteFilter}] {
    "url": fullPath,
    lastModifiedBy,
    "lastModified": select(
      lastModifiedBy == "officeContent" => officeContentUpdatedAt,
      lastModifiedBy == "page" => pageUpdatedAt,
      lastModifiedBy == "routeNode" => routeUpdatedAt,
      true => null
    )
  }
}`;

export { SitemapQuery };
